import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ProjectTemplate } from '@/hooks/useProjects';
import { useAuth } from '@/hooks/useAuth';
import { useErrorReporting } from '@/hooks/useErrorReporting';

interface TemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  template?: ProjectTemplate | null;
  onSave: () => void;
}

interface Field {
  id: string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'date' | 'checkbox' | 'select';
  options?: string[];
}

interface Section {
  title: string;
  fields: Field[];
}

export const TemplateModal: React.FC<TemplateModalProps> = ({
  isOpen,
  onClose,
  template,
  onSave
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [sections, setSections] = useState<Section[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { reportError } = useErrorReporting();

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        description: template.description || ''
      });
      setSections(template.schema?.sections || []);
    } else {
      setFormData({
        name: '',
        description: ''
      });
      setSections([]);
    }
  }, [template]);

  const addSection = () => {
    setSections(prev => [...prev, { title: '', fields: [] }]);
  };

  const updateSection = (index: number, title: string) => {
    setSections(prev => prev.map((section, i) => 
      i === index ? { ...section, title } : section
    ));
  };

  const deleteSection = (index: number) => {
    setSections(prev => prev.filter((_, i) => i !== index));
  };

  const addField = (sectionIndex: number) => {
    setSections(prev => prev.map((section, i) => 
      i === sectionIndex 
        ? { ...section, fields: [...section.fields, { id: '', label: '', type: 'text' }] }
        : section
    ));
  };

  const updateField = (sectionIndex: number, fieldIndex: number, field: Partial<Field>) => {
    setSections(prev => prev.map((section, i) => 
      i === sectionIndex 
        ? { 
            ...section, 
            fields: section.fields.map((f, j) => 
              j === fieldIndex ? { ...f, ...field } : f
            )
          }
        : section
    ));
  };

  const deleteField = (sectionIndex: number, fieldIndex: number) => {
    setSections(prev => prev.map((section, i) => 
      i === sectionIndex 
        ? { ...section, fields: section.fields.filter((_, j) => j !== fieldIndex) }
        : section
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      const errorMsg = 'Je moet ingelogd zijn om een template op te slaan.';
      toast({
        title: 'Fout',
        description: errorMsg,
        variant: 'destructive'
      });
      await reportError(errorMsg, new Error('User not authenticated'), { action: 'template_save' });
      return;
    }

    // Validation
    const validationErrors: string[] = [];
    sections.forEach((section, sectionIndex) => {
      if (!section.title.trim()) {
        validationErrors.push(`Sectie ${sectionIndex + 1} heeft geen titel`);
      }
      section.fields.forEach((field, fieldIndex) => {
        if (!field.id.trim()) {
          validationErrors.push(`Veld ${fieldIndex + 1} in sectie "${section.title}" mist een ID`);
        }
        if (!field.label.trim()) {
          validationErrors.push(`Veld ${fieldIndex + 1} in sectie "${section.title}" mist een label`);
        }
        if (field.type === 'select' && (!field.options || field.options.length === 0)) {
          validationErrors.push(`Dropdown veld "${field.label}" in sectie "${section.title}" mist opties`);
        }
      });
    });

    if (validationErrors.length > 0) {
      console.error('[TEMPLATE] Validation errors:', validationErrors);
      toast({
        title: 'Validatie fouten',
        description: validationErrors.join('. '),
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    console.log('[TEMPLATE] Submitting template:', { formData, sections, isEdit: !!template });

    try {
      const templateData = {
        name: formData.name,
        description: formData.description || null,
        schema: { sections } as any,
        created_by: user.id
      };

      console.log('[TEMPLATE] Processed template data:', templateData);

      if (template) {
        const { data, error } = await supabase
          .from('project_templates')
          .update(templateData)
          .eq('id', template.id)
          .select();

        if (error) throw error;

        console.log('[TEMPLATE] Update response:', data);
        toast({
          title: 'Template bijgewerkt',
          description: 'De template is succesvol bijgewerkt.',
        });
      } else {
        const { data, error } = await supabase
          .from('project_templates')
          .insert(templateData)
          .select();

        if (error) throw error;

        console.log('[TEMPLATE] Insert response:', data);
        toast({
          title: 'Template aangemaakt',
          description: 'De template is succesvol aangemaakt.',
        });
      }

      onSave();
      onClose();
    } catch (error: any) {
      console.error('[TEMPLATE] Error saving template:', error);
      
      const errorMsg = `Er ging iets mis bij het opslaan van de template. Details: ${error.message}`;
      toast({
        title: 'Fout bij opslaan',
        description: (
          <div className="space-y-2">
            <div>{errorMsg}</div>
            <Button
              onClick={() => reportError(errorMsg, error, { 
                action: 'template_save', 
                templateData: { formData, sections } 
              })}
              variant="outline"
              size="sm"
              className="w-full"
            >
              Meld aan beheerder
            </Button>
          </div>
        ),
        variant: 'destructive',
        duration: 8000,
      });
      
      // Also report automatically
      await reportError(errorMsg, error, { 
        action: 'template_save', 
        templateData: { formData, sections } 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {template ? 'Template Bewerken' : 'Nieuwe Template'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label htmlFor="name">Template Naam *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label htmlFor="description">Beschrijving</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Template Secties</h3>
              <Button type="button" onClick={addSection} variant="outline" size="sm">
                <Plus className="mr-2 h-4 w-4" />
                Sectie Toevoegen
              </Button>
            </div>

            {sections.map((section, sectionIndex) => (
              <Card key={sectionIndex}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Sectie titel"
                      value={section.title}
                      onChange={(e) => updateSection(sectionIndex, e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      onClick={() => deleteSection(sectionIndex)}
                      variant="outline"
                      size="sm"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {section.fields.map((field, fieldIndex) => (
                      <div key={fieldIndex} className="flex items-end gap-2 p-3 bg-muted rounded-lg">
                        <div className="flex-1">
                          <Label>Veld ID</Label>
                          <Input
                            placeholder="field_id"
                            value={field.id}
                            onChange={(e) => updateField(sectionIndex, fieldIndex, { id: e.target.value })}
                          />
                        </div>
                        <div className="flex-1">
                          <Label>Label</Label>
                          <Input
                            placeholder="Veld label"
                            value={field.label}
                            onChange={(e) => updateField(sectionIndex, fieldIndex, { label: e.target.value })}
                          />
                        </div>
                        <div className="flex-1">
                          <Label>Type</Label>
                          <Select
                            value={field.type}
                            onValueChange={(value: any) => updateField(sectionIndex, fieldIndex, { type: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="text">Tekst</SelectItem>
                              <SelectItem value="textarea">Tekstvak</SelectItem>
                              <SelectItem value="number">Nummer</SelectItem>
                              <SelectItem value="date">Datum</SelectItem>
                              <SelectItem value="checkbox">Checkbox</SelectItem>
                              <SelectItem value="select">Dropdown</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {field.type === 'select' && (
                          <div className="flex-1">
                            <Label>Opties (komma gescheiden)</Label>
                            <Input
                              placeholder="Optie 1, Optie 2, Optie 3"
                              value={field.options?.join(', ') || ''}
                              onChange={(e) => updateField(sectionIndex, fieldIndex, { 
                                options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) 
                              })}
                            />
                          </div>
                        )}
                        <Button
                          type="button"
                          onClick={() => deleteField(sectionIndex, fieldIndex)}
                          variant="outline"
                          size="sm"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    
                    <Button
                      type="button"
                      onClick={() => addField(sectionIndex)}
                      variant="outline"
                      size="sm"
                      className="w-full"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Veld Toevoegen
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}

            {sections.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                Geen secties gevonden. Voeg een sectie toe om te beginnen.
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuleren
            </Button>
            <Button type="submit" disabled={loading || !formData.name}>
              {loading ? 'Opslaan...' : template ? 'Bijwerken' : 'Aanmaken'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
